import { useMemo } from 'react'
import { useMobile } from '../utils/mobile'

interface ComputedHeightParams {
  hasStartedChat: boolean
  isInputFocused: boolean
  suggestionsCount: number
  showSuggestions?: boolean
  chatHeight?: number  // NEW: height from useContentHeight
}

interface HeightBreakdown {
  input: number
  suggestions: number
  padding: number
  total: number
}

export function useComputedHeight({
  hasStartedChat,
  isInputFocused,
  suggestionsCount,
  showSuggestions = true,
  chatHeight
}: ComputedHeightParams) {
  const isMobile = useMobile()

  const computedHeight = useMemo(() => {
    // Component heights (matched to actual rendered sizes)
    // AIChatInput: 56px base container + 40px wrapper padding (pt-4 + pb-6)
    const INPUT_SECTION_HEIGHT = 96
    const SUGGESTION_ITEM_HEIGHT = 48 // Button height + gap

    // Calculate suggestion height - only show when input is focused
    const getSuggestionsHeight = () => {
      if (!showSuggestions || suggestionsCount === 0 || !isInputFocused) return 0
      
      // Calculate total suggestions height with proper spacing
      const visibleSuggestions = Math.min(suggestionsCount, 3) // Max 3 suggestions
      const suggestionGap = 8 // gap-2 between items
      const topMargin = 12 // mt-3 from component
      const bottomBuffer = 40 // Extra breathing room
      
      return (visibleSuggestions * SUGGESTION_ITEM_HEIGHT) + 
             ((visibleSuggestions - 1) * suggestionGap) + 
             topMargin + bottomBuffer
    }

    // Mobile vs Desktop differences
    const mobileAdjustment = isMobile ? -10 : 0 // Slightly more compact on mobile
    const focusExpansion = isMobile && isInputFocused ? 30 : 0 // Extra space when focused on mobile

    let height: number
    let breakdown: HeightBreakdown

    if (hasStartedChat) {
      // Chat mode: use the height from useContentHeight
      // chatHeight already includes everything (chat area + input)
      height = chatHeight || 696 // Fallback if not provided
      breakdown = {
        input: INPUT_SECTION_HEIGHT,
        suggestions: 0,
        padding: mobileAdjustment,
        total: height
      }
    } else {
      // Initial mode: input + suggestions (only when focused)
      const suggestionsHeight = getSuggestionsHeight()
      
      height = INPUT_SECTION_HEIGHT + suggestionsHeight + mobileAdjustment + focusExpansion
      
      breakdown = {
        input: INPUT_SECTION_HEIGHT + focusExpansion,
        suggestions: suggestionsHeight,
        padding: mobileAdjustment,
        total: height
      }
    }

    // Min/Max heights are safety constraints:
    // - MIN: Ensures iframe doesn't become unusably small (browser/CSS edge cases)
    // - MAX: Prevents iframe from overwhelming small viewports (mobile UX)
    // In most cases, the calculated height should be within these bounds naturally
    const MIN_HEIGHT = isMobile ? 100 : 120
    const MAX_HEIGHT = isMobile ? 500 : 850
    
    // height = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height))

    return {
      height,
      breakdown,
      isMobile,
      isCompact: isMobile && !isInputFocused && !hasStartedChat
    }
  }, [hasStartedChat, isInputFocused, suggestionsCount, showSuggestions, isMobile, chatHeight])

  return computedHeight
} 